import { Inject, Injectable, DOCUMENT } from '@angular/core';

import tinycolor from 'tinycolor2';

import CONSTANTS from '@constants';
import { IFBColorTheme } from '@app/models/color-theme';

@Injectable({
  providedIn: 'root'
})
export class ColorThemeService {
  currentTheme: IFBColorTheme;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  init(theme = CONSTANTS.COLOR_THEMES[0]) {
    this.applyTheme(theme);
  }

  get defaultTheme(): boolean {
    return this.currentTheme.name === CONSTANTS.COLOR_THEMES[0].name;
  }

  applyTheme(theme: IFBColorTheme = CONSTANTS.COLOR_THEMES[0]): void {
    this.currentTheme = { ...theme };
    this.setCssVariables();
  }

  private setCssVariable(name: string, value: string): void {
    this.document.querySelector<HTMLElement>(':root').style.setProperty(name, value);
  }

  /*
    Sets the following global CSS variables:

    --theme-button = theme.button
    --theme-button__darken-3 = darken(theme.button, 3)
    --theme-button__darken-10 = darken(theme.button, 10)
    --theme-button__alpha-10 = rgba(theme.button, 0.1)
    --theme-button__alpha-20 = rgba(theme.button, 0.2)

    For usages see _color-theme.scss
  */
  private setCssVariables(): void {
    this.setCssVariable('--theme-button', this.currentTheme.button);
    this.currentTheme.textButton = this.maxContrast(this.currentTheme.button);
    this.setCssVariable('--theme-text-button', this.currentTheme.textButton);
    this.setCssVariable('--theme-button__darken-3', this.darken(this.currentTheme.button, 3));
    this.setCssVariable('--theme-button__darken-10', this.darken(this.currentTheme.button, 10));
    this.setCssVariable('--theme-button__alpha-10', this.rgba(this.currentTheme.button, 0.1));
    this.setCssVariable('--theme-button__alpha-20', this.rgba(this.currentTheme.button, 0.2));
  }

  darken(color: string, amount?: number): string {
    return tinycolor(color).darken(amount).toString();
  }

  rgba(color: string, alpha: number): string {
    return tinycolor(color).setAlpha(alpha).toRgbString();
  }

  private maxContrast(color: string): string {
    const { r, g, b } = this.toRGB(color);
    const contrastColor = this.brightness(r, g, b) < 128 ? '#ffffff' : '#000000';

    return contrastColor;
  }

  private toRGB(hex: string): { r: number; g: number; b: number } {
    const parsedHex = hex.replace('#', '');
    const r = parseInt(parsedHex.substring(0, 2), 16);
    const g = parseInt(parsedHex.substring(2, 4), 16);
    const b = parseInt(parsedHex.substring(4, 6), 16);
    return { r, g, b };
  }

  private brightness(r: number, g: number, b: number): number {
    return Math.round((r * 299 + g * 587 + b * 114) / 1000);
  }
}
